import React, { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { Label } from "../../ui/label";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import {
  ClipboardList,
  Loader2,
  X,
  Plus,
  Trash2,
  Edit,
  Save,
  GraduationCap,
  Calculator,
  Maximize2,
  Minimize2
} from "lucide-react";
import {
  turmasNotasAPI,
  ItemAvaliacao,
  MembroComNotas,
  CreateItemAvaliacaoDTO
} from "../../../services/turmasNotas";
import { InputText } from "../../InputText";

interface GerenciarNotasModalProps {
  isOpen: boolean;
  onClose: () => void;
  turma: {
    id: string;
    nome: string;
  };
}

type TabType = 'itens' | 'notas';

export function GerenciarNotasModal({
  isOpen,
  onClose,
  turma
}: GerenciarNotasModalProps) {
  const [activeTab, setActiveTab] = useState<TabType>('itens');
  const [loading, setLoading] = useState(false);
  const [savingNotas, setSavingNotas] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Estados para Itens de Avaliação
  const [itens, setItens] = useState<ItemAvaliacao[]>([]);
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [novoItem, setNovoItem] = useState<CreateItemAvaliacaoDTO>({
    titulo: '',
    descricao: '',
    valor_maximo: 10,
    peso: 1,
  });

  // Estados para Notas
  const [membrosComNotas, setMembrosComNotas] = useState<MembroComNotas[]>([]);
  const [notasAlteradas, setNotasAlteradas] = useState<Map<string, number | null>>(new Map());

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen, turma.id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [itensData, notasData] = await Promise.all([
        turmasNotasAPI.listarItensAvaliacao(turma.id),
        turmasNotasAPI.listarNotasPorTurma(turma.id),
      ]);
      setItens(itensData);
      setMembrosComNotas(notasData);
      setNotasAlteradas(new Map());
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      enqueueSnackbar('Erro ao carregar dados das notas', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // ============ ITENS DE AVALIAÇÃO ============

  const handleAddItem = async () => {
    if (!novoItem.titulo.trim()) {
      enqueueSnackbar('Título é obrigatório', { variant: 'warning' });
      return;
    }

    try {
      setLoading(true);
      await turmasNotasAPI.criarItemAvaliacao(turma.id, novoItem);
      enqueueSnackbar('Item de avaliação criado!', { variant: 'success' });
      setIsAddingItem(false);
      setNovoItem({ titulo: '', descricao: '', valor_maximo: 10, peso: 1 });
      await loadData();
    } catch (error) {
      enqueueSnackbar('Erro ao criar item de avaliação', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateItem = async (item: ItemAvaliacao) => {
    try {
      setLoading(true);
      await turmasNotasAPI.atualizarItemAvaliacao(item.id, {
        titulo: item.titulo,
        descricao: item.descricao,
        valor_maximo: item.valor_maximo,
        peso: item.peso,
      });
      enqueueSnackbar('Item atualizado!', { variant: 'success' });
      setEditingItemId(null);
      await loadData();
    } catch (error) {
      enqueueSnackbar('Erro ao atualizar item', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteItem = async (id: string) => {
    if (!window.confirm('Deseja excluir este item? Todas as notas associadas serão perdidas.')) {
      return;
    }

    try {
      setLoading(true);
      await turmasNotasAPI.excluirItemAvaliacao(id);
      enqueueSnackbar('Item excluído!', { variant: 'success' });
      await loadData();
    } catch (error) {
      enqueueSnackbar('Erro ao excluir item', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // ============ NOTAS ============

  const handleNotaChange = (idMembro: string, idItem: string, valor: string) => {
    const key = `${idMembro}|${idItem}`;
    const numericValue = valor === '' ? null : parseFloat(valor);

    setNotasAlteradas(prev => {
      const newMap = new Map(prev);
      newMap.set(key, numericValue);
      return newMap;
    });

    // Atualizar estado local para visualização imediata
    setMembrosComNotas(prev => prev.map(membro => {
      if (membro.id !== idMembro) return membro;

      return {
        ...membro,
        notas: membro.notas.map(nota => {
          if (nota.id_item_avaliacao !== idItem) return nota;
          return { ...nota, valor: numericValue };
        }),
      };
    }));
  };

  const getNotaValue = (membro: MembroComNotas, idItem: string): string => {
    const nota = membro.notas.find(n => n.id_item_avaliacao === idItem);
    if (nota?.valor === null || nota?.valor === undefined) return '';
    return nota.valor.toString();
  };

  const handleSaveNotas = async () => {
    if (notasAlteradas.size === 0) {
      enqueueSnackbar('Nenhuma nota foi alterada', { variant: 'info' });
      return;
    }

    try {
      setSavingNotas(true);

      const notasParaSalvar = Array.from(notasAlteradas.entries()).map(([key, valor]) => {
        const [idMembro, idItem] = key.split('|');
        return {
          id_membro: idMembro,
          id_item_avaliacao: idItem,
          valor,
        };
      });

      await turmasNotasAPI.lancarNotasEmLote(notasParaSalvar);
      enqueueSnackbar('Notas salvas com sucesso!', { variant: 'success' });
      setNotasAlteradas(new Map());
      await loadData();
    } catch (error) {
      enqueueSnackbar('Erro ao salvar notas', { variant: 'error' });
    } finally {
      setSavingNotas(false);
    }
  };

  const calcularTotal = (membro: MembroComNotas): string => {
    let somaObtida = 0;
    let somaMaxima = 0;
    let temNota = false;

    for (const item of itens) {
      somaMaxima += Number(item.valor_maximo);
      const nota = membro.notas.find(n => n.id_item_avaliacao === item.id);
      if (nota && nota.valor !== null) {
        somaObtida += Number(nota.valor);
        temNota = true;
      }
    }

    if (!temNota) return '-';
    return `${somaObtida.toFixed(1).replace('.', ',')}/${somaMaxima}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <Card className={`mx-4 flex flex-col transition-all duration-300 ${
        isExpanded
          ? 'w-full h-full max-w-none max-h-full rounded-none'
          : 'w-full max-w-6xl max-h-[90vh]'
      }`}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div className="flex-1">
            <CardTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-[oklch(0.45_0.15_142)]" />
              Notas - {turma.nome}
            </CardTitle>
            <CardDescription>
              Gerencie os itens de avaliação e lance as notas dos membros
            </CardDescription>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="h-6 w-6 p-0"
              title={isExpanded ? 'Retrair' : 'Expandir'}
            >
              {isExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-6 w-6 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>

        {/* Tabs */}
        <div className="border-b px-6">
          <nav className="flex space-x-4">
            <button
              onClick={() => setActiveTab('itens')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'itens'
                  ? 'border-[oklch(0.45_0.15_142)] text-[oklch(0.45_0.15_142)]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center gap-2">
                <ClipboardList className="h-4 w-4" />
                Itens de Avaliação ({itens.length})
              </div>
            </button>
            <button
              onClick={() => setActiveTab('notas')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'notas'
                  ? 'border-[oklch(0.45_0.15_142)] text-[oklch(0.45_0.15_142)]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center gap-2">
                <Calculator className="h-4 w-4" />
                Lançar Notas ({membrosComNotas.length} membros)
              </div>
            </button>
          </nav>
        </div>

        <CardContent className="flex-1 overflow-y-auto pt-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[oklch(0.45_0.15_142)]" />
            </div>
          ) : activeTab === 'itens' ? (
            // ============ ABA ITENS DE AVALIAÇÃO ============
            <div className="space-y-4">
              {/* Botão Adicionar */}
              {!isAddingItem && (
                <Button
                  onClick={() => setIsAddingItem(true)}
                  className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Adicionar Item
                </Button>
              )}

              {/* Form Adicionar Item */}
              {isAddingItem && (
                <div className="border rounded-lg p-4 bg-muted/50 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="titulo">Título *</Label>
                      <InputText
                        id="titulo"
                        name="titulo"
                        title=""
                        value={novoItem.titulo}
                        onChange={(e) => setNovoItem({ ...novoItem, titulo: e.target.value })}
                        placeholder="Ex: Prova Teórica"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="descricao">Descrição</Label>
                      <InputText
                        id="descricao"
                        name="descricao"
                        title=""
                        value={novoItem.descricao || ''}
                        onChange={(e) => setNovoItem({ ...novoItem, descricao: e.target.value })}
                        placeholder="Descrição opcional"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="valor_maximo">Valor Máximo</Label>
                      <input
                        type="number"
                        id="valor_maximo"
                        value={novoItem.valor_maximo}
                        onChange={(e) => setNovoItem({ ...novoItem, valor_maximo: parseFloat(e.target.value) || 10 })}
                        className="w-full mt-1 px-3 py-2 border border-border rounded-md bg-background"
                        min="1"
                        step="0.5"
                      />
                    </div>
                    <div>
                      <Label htmlFor="peso">Peso</Label>
                      <input
                        type="number"
                        id="peso"
                        value={novoItem.peso}
                        onChange={(e) => setNovoItem({ ...novoItem, peso: parseFloat(e.target.value) || 1 })}
                        className="w-full mt-1 px-3 py-2 border border-border rounded-md bg-background"
                        min="0.1"
                        step="0.1"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleAddItem} className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white">
                      <Save className="h-4 w-4 mr-2" />
                      Salvar
                    </Button>
                    <Button variant="outline" onClick={() => setIsAddingItem(false)}>
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}

              {/* Lista de Itens */}
              {itens.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <ClipboardList className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Nenhum item de avaliação cadastrado</p>
                  <p className="text-sm">Adicione itens para poder lançar notas</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {itens.map((item) => (
                    <div
                      key={item.id}
                      className="border rounded-lg p-4 flex items-center justify-between hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="font-medium">{item.titulo}</div>
                        {item.descricao && (
                          <p className="text-sm text-muted-foreground mt-1">{item.descricao}</p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="secondary">Máx: {item.valor_maximo}</Badge>
                          <Badge variant="outline">Peso: {item.peso}</Badge>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteItem(item.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            // ============ ABA LANÇAR NOTAS ============
            <div className="space-y-4">
              {itens.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <ClipboardList className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Nenhum item de avaliação cadastrado</p>
                  <p className="text-sm">Adicione itens na aba "Itens de Avaliação" primeiro</p>
                </div>
              ) : membrosComNotas.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <GraduationCap className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Nenhum membro na turma</p>
                  <p className="text-sm">Adicione membros à turma para lançar notas</p>
                </div>
              ) : (
                <>
                  {/* Botão Salvar */}
                  <div className="flex justify-end">
                    <Button
                      onClick={handleSaveNotas}
                      disabled={savingNotas || notasAlteradas.size === 0}
                      className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
                    >
                      {savingNotas ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4 mr-2" />
                      )}
                      Salvar Notas
                      {notasAlteradas.size > 0 && (
                        <Badge className="ml-2 bg-white text-[oklch(0.45_0.15_142)]">
                          {notasAlteradas.size}
                        </Badge>
                      )}
                    </Button>
                  </div>

                  {/* Tabela de Notas */}
                  <div className="border rounded-lg overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium text-xs">Membro</th>
                          {itens.map((item) => (
                            <th key={item.id} className="px-3 py-2 text-center font-medium text-xs min-w-[80px]">
                              <div>{item.titulo}</div>
                              <div className="text-[10px] text-muted-foreground font-normal">
                                (0-{item.valor_maximo})
                              </div>
                            </th>
                          ))}
                          <th className="px-3 py-2 text-center font-medium text-xs bg-muted/80">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {membrosComNotas.map((membro, index) => (
                          <tr key={membro.id} className={index % 2 === 0 ? 'bg-background' : 'bg-muted/30'}>
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-1">
                                {membro.militar.patente && (
                                  <span className="text-[10px] text-muted-foreground">
                                    {membro.militar.patente.abreviacao}
                                  </span>
                                )}
                                <span className="font-medium text-xs">{membro.militar.nick}</span>
                              </div>
                            </td>
                            {itens.map((item) => (
                              <td key={item.id} className="px-1 py-1 text-center">
                                <input
                                  type="number"
                                  value={getNotaValue(membro, item.id)}
                                  onChange={(e) => handleNotaChange(membro.id, item.id, e.target.value)}
                                  className="w-16 px-1 py-1 text-center text-xs border border-border rounded-md bg-background focus:ring-2 focus:ring-[oklch(0.45_0.15_142)] focus:border-transparent"
                                  min="0"
                                  max={item.valor_maximo}
                                  step="0.1"
                                  placeholder="-"
                                />
                              </td>
                            ))}
                            <td className="px-3 py-2 text-center font-bold text-xs bg-muted/50">
                              {calcularTotal(membro)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
